import type { FlowStep, StepRuntime } from "../types";
import { FlowStepError, PollTimeoutError, StepSkippedError } from "../engine/errors";
import { requireEnquiry, verifyEq, waitForEventWithGate, waitForSnapshot } from "./common";

export const STEP_DRAFT_ESTIMATE_SHARED = "draft_estimate_shared";
export const STEP_DRAFT_ESTIMATE_RESPONDED = "draft_estimate_responded";

const DRAFT_ESTIMATE_SHARED = "DRAFT_ESTIMATE_SHARED";
const DRAFT_ESTIMATE_RESPONDED = "DRAFT_ESTIMATE_RESPONDED";

/** Latest shared DRAFT_ESTIMATE of the enquiry, newest first. */
const findLatestDraftEstimateNo = async (rt: StepRuntime): Promise<string | undefined> => {
  const enquiry = requireEnquiry(rt.ctx);
  const res = await rt.api<{ estimates?: any[] }>({
    label: "GET draft estimates",
    method: "GET",
    path: `/v1/estimate?enquiryNo=${enquiry.orderNo}&status=SHARED&limit=50`,
  });
  const time = (e: any) => new Date(e?.estimateSharedAt || e?.createdAt || 0).getTime();
  const drafts = (res.data?.estimates || [])
    .filter((e) => e?.type === "DRAFT_ESTIMATE")
    .sort((a, b) => time(b) - time(a));
  return drafts[0]?.estimateNo;
};

export const draftEstimateSharedStep: FlowStep = {
  id: STEP_DRAFT_ESTIMATE_SHARED,
  name: "Draft estimate shared",
  targetLabel: "Draft estimate shared",
  description:
    "Require Price Estimate on: after part conversion the backend shares a DRAFT_ESTIMATE (Atlas ENQUIRY_PART_CONVERSION_COMPLETED → POST /v1/estimate/:enquiryNo/share-draft-estimate) and moves the enquiry to DRAFT_ESTIMATE_SHARED. Waits for that; on timeout the draft can be shared from here. Skipped when Require Price Estimate is off.",
  actor: "EVENT",
  kind: "event",
  estimateMs: [3000, 30000],
  shouldSkip: (ctx) =>
    ctx.config.requirePriceEstimate
      ? null
      : "Require Price Estimate is off: the enquiry goes straight to DRAFT_ESTIMATE_RESPONDED",
  execute: async (rt) => {
    const enquiry = requireEnquiry(rt.ctx);
    const reached = (status?: string) => status === DRAFT_ESTIMATE_SHARED || status === DRAFT_ESTIMATE_RESPONDED;

    if (reached(rt.ctx.snapshot?.enquiry?.orderStatus)) {
      rt.log(`Enquiry already ${rt.ctx.snapshot?.enquiry?.orderStatus}`, "info");
    } else {
      await waitForEventWithGate(rt, {
        trigger: "ENQUIRY_PART_CONVERSION_COMPLETED → Atlas → share-draft-estimate",
        expected: `enquiry.orderStatus = ${DRAFT_ESTIMATE_SHARED}`,
        check: (s) => reached(s.enquiry?.orderStatus),
        describe: (s) => `enquiry=${s.enquiry?.orderStatus}`,
        allowContinue: false,
        fallback: {
          label: "Share the draft from here",
          description: "POST /v1/estimate/:enquiryNo/share-draft-estimate (what the trigger calls)",
          run: async () => {
            await rt.api({
              label: "Share draft estimate",
              method: "POST",
              path: `/v1/estimate/${enquiry.orderNo}/share-draft-estimate`,
              body: {},
            });
          },
        },
      });
    }

    const estimateNo = await findLatestDraftEstimateNo(rt);
    if (!estimateNo) {
      throw new FlowStepError("The enquiry has no shared DRAFT_ESTIMATE", {
        expected: "A SHARED estimate with type DRAFT_ESTIMATE",
        actual: "none found",
      });
    }
    rt.ctx.draftEstimate = { estimateNo };
    rt.log(`Draft estimate ${estimateNo} shared`, "success");
  },
  verify: (s) =>
    verifyEq("enquiry.orderStatus", [DRAFT_ESTIMATE_SHARED, DRAFT_ESTIMATE_RESPONDED], s.enquiry?.orderStatus),
};

export const draftEstimateRespondedStep: FlowStep = {
  id: STEP_DRAFT_ESTIMATE_RESPONDED,
  name: "Draft estimate responded",
  targetLabel: "Draft estimate responded",
  description:
    "Require Price Estimate on: the customer confirms the draft (POST /v1/estimate/:estimateNo/request-final-estimate) → DRAFT_ESTIMATE_RESPONDED, draft split CONFIRMED. Off: the backend moves the enquiry there itself after part conversion and this step only checks it (skipped on a backend that leaves it in PART_CONVERSION_COMPLETED).",
  actor: "CUSTOMER",
  kind: "api",
  estimateMs: [500, 4000],
  execute: async (rt) => {
    const status = rt.ctx.snapshot?.enquiry?.orderStatus;
    if (status === DRAFT_ESTIMATE_RESPONDED) {
      rt.log(`Enquiry already ${DRAFT_ESTIMATE_RESPONDED}`, "info");
      return;
    }

    if (!rt.ctx.config.requirePriceEstimate) {
      try {
        await waitForSnapshot(rt, {
          trigger: "Backend: PART_CONVERSION_COMPLETED → DRAFT_ESTIMATE_RESPONDED",
          expected: `enquiry.orderStatus = ${DRAFT_ESTIMATE_RESPONDED}`,
          check: (s) => s.enquiry?.orderStatus === DRAFT_ESTIMATE_RESPONDED,
          describe: (s) => `enquiry=${s.enquiry?.orderStatus}`,
          maxAttempts: 2,
          scope: { enquiry: true },
        });
        return;
      } catch (err) {
        if (err instanceof PollTimeoutError) {
          throw new StepSkippedError(
            `Enquiry stayed ${rt.ctx.snapshot?.enquiry?.orderStatus}: this backend does not move it to ${DRAFT_ESTIMATE_RESPONDED} automatically`,
          );
        }
        throw err;
      }
    }

    const estimateNo = rt.ctx.draftEstimate?.estimateNo || (await findLatestDraftEstimateNo(rt));
    if (!estimateNo) {
      throw new FlowStepError("No shared DRAFT_ESTIMATE to confirm; run 'Draft estimate shared' first");
    }
    const res = await rt.api<{ orderStatus?: string; respondedAt?: string }>({
      label: "Respond to draft estimate",
      method: "POST",
      path: `/v1/estimate/${estimateNo}/request-final-estimate`,
      body: {},
      actor: "CUSTOMER",
    });
    rt.log(
      `Draft estimate ${estimateNo} responded at ${res.data?.respondedAt ?? "?"} (enquiry ${res.data?.orderStatus ?? "?"})`,
      "success",
    );
  },
  verify: (s) => verifyEq("enquiry.orderStatus", DRAFT_ESTIMATE_RESPONDED, s.enquiry?.orderStatus),
};
